import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceAnchor,
  ExperienceAnchorType,
  IExperienceTemplate,
  ISupportingActor,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import {
  ExperiencePreview,
  ExperiencePreviewMode,
  PreviewGroup,
  PreviewGroupType,
  PreviewItemType
} from '@dadohr/shared/domainTypes/experiencePreview';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { ColorTheme } from '@dadohr/shared/domainTypes/theme';
import { Timestamp } from '@dadohr/shared/domainTypes/time';
import { IUser } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import {
  Button,
  ButtonGroup,
  ButtonProps,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  Switch,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { capitalize, compact, keyBy, sortBy, uniqBy } from 'lodash';
import {
  AlertCircle,
  ExternalLink,
  Info,
  Settings,
  Users,
  X
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AutocompleteUser } from '../../../../../components/AutocompleteUser';
import { DialogActionsHeader } from '../../../../../components/DialogActionsHeader';
import { FormRow } from '../../../../../components/Form/FormRow';
import { InfoBox } from '../../../../../components/InfoBox';
import { Loader } from '../../../../../components/Loader';
import { TokenDefinition } from '../../../../../components/MarkdownText';
import { OrgLogo } from '../../../../../components/OrgLogo';
import { SomethingWentWrongWithReport } from '../../../../../components/SomethingWentWrong';
import { DateTimeFieldNullable } from '../../../../../components/Time/DateTimeField';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { useSnackbar } from '../../../../../hooks/useSnackbar';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../../i18next';
import { FlexContainer, FlexItem } from '../../../../../layout/Flex';
import { Page } from '../../../../../layout/Page';
import { useQueryParam } from '../../../../../routes';
import { useCurrentUser } from '../../../../../services/currentUser';
import { generateAccessToken } from '../../../../../services/experiences/instance';
import { updateDoc } from '../../../../../services/firebase';
import { LightMdToJsx } from '../../../../../services/formatter';
import { isTimestamp, now, tsFromMillis } from '../../../../../services/time';
import { createEmbeddableIframeForUrl } from '../../../../../services/url';
import {
  getUser,
  getUserValue,
  useUsersByOrgId
} from '../../../../../services/user';
import { useTheme } from '../../../../../themes';
import {
  ActorDescriptor,
  useActorDescriptorsFromTemplate
} from '../../../components/Actors';
import {
  AnchorField,
  getAnchorFields,
  useAnchorFields
} from '../../../components/Anchors';
import { useTokenDefinitionsForExperience } from '../../../components/Builders/TaskBuilder/components/BlockSettings/common';
import { StandardDrawer } from '../../../components/StandardDrawer';
import { PreviewFilters, PreviewGroupComponent } from './components';
import {
  calculateInitalAnchorDate,
  getMiniMalTaksBuilderContext,
  getPreviewGroups as getExperiencePreview
} from './service';
import { PreviewFilterState, PreviewOptions } from './types';
import { previewBatchToKey } from './utils';

type ExperiencePreviewContext = {
  mode: ExperiencePreviewMode;
  anchorDate: Timestamp | null;
  currentTemplate: Doc<IExperienceTemplate>;
  currentParticipantUserId: string | null;
};

type ExperiencePreviewParams = Pick<
  ExperiencePreviewContext,
  'anchorDate' | 'currentParticipantUserId' | 'mode'
>;

const ButtonsContainer = styled(ButtonGroup)((p) => ({
  paddingTop: p.theme.spacing(1),
  paddingBottom: p.theme.spacing(2)
}));

const LogoContainer = styled('div')((p) => ({
  position: 'absolute',
  right: p.theme.spacing(2),
  bottom: p.theme.spacing(2)
}));

const PreviewModeSelector = ({
  value,
  onChange
}: {
  value: ExperiencePreviewMode;
  onChange: (mode: ExperiencePreviewMode) => void;
}) => {
  const { t } = useUnsafeTranslation();

  const getButtonVariantForMode = (buttonMode: ExperiencePreviewMode) =>
    value === buttonMode ? 'contained' : 'outlined';
  const getButtonColorForMode = (buttonMode: ExperiencePreviewMode) =>
    value === buttonMode ? 'primary' : 'default';

  const options: {
    value: ExperiencePreviewMode;
    text: ReturnType<typeof t>;
  }[] = [
    {
      value: ExperiencePreviewMode.EXPERIENCE,
      text: t('Preview full experience')
    },
    {
      value: ExperiencePreviewMode.INSTANCE,
      text: t('Preview as individual')
    }
  ];

  return (
    <ButtonsContainer fullWidth={true}>
      {options.map((option) => (
        <Button
          key={option.value}
          value={option.value}
          startIcon={<Users size={16} />}
          variant={getButtonVariantForMode(option.value)}
          color={getButtonColorForMode(option.value)}
          onClick={() => onChange(option.value)}
        >
          {option.text}
        </Button>
      ))}
    </ButtonsContainer>
  );
};

const useAnchorFieldLabelOrFallback = (
  anchorFields: AnchorField[],
  anchor: ExperienceAnchor,
  t: UnsafeTFunction
) => {
  if (anchor.type === ExperienceAnchorType.EMPLOYEE_DATE) {
    const field = anchorFields.find((a) => a.field === anchor.field);
    if (field) {
      return capitalize(field.shortLabel(t));
    }
  }
  return t('Experience start');
};

export const CustomizePreviewHeader = ({
  onClose
}: {
  onClose: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <FlexContainer justifyContent="space-between" marginBottom={2}>
      <Typography variant="h4">{t('Customize Preview Data')}</Typography>
      <IconButton onClick={onClose}>
        <X size={20} />
      </IconButton>
    </FlexContainer>
  );
};
export const CustomizePreviewBody = styled('div')((p) => ({
  marginTop: p.theme.spacing(3)
}));

export const CustomizePreviewDrawer = ({
  org,
  open,
  experiencePreviewContext,
  onClose,
  onSave
}: {
  org: IOrganization;
  open: boolean;
  experiencePreviewContext: ExperiencePreviewContext;
  onClose: () => void;
  onSave: (experiencePreviewContext: ExperiencePreviewContext) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { tz } = useCurrentUser();
  const [users, loading, error] = useUsersByOrgId(org.id);
  const anchorFields = useAnchorFields(org.customUserFields);
  const anchorLabel = useAnchorFieldLabelOrFallback(
    anchorFields,
    experiencePreviewContext.currentTemplate.data.anchor,
    t
  );

  const userAnchorField =
    experiencePreviewContext.currentTemplate.data.anchor.type ===
    ExperienceAnchorType.EMPLOYEE_DATE
      ? experiencePreviewContext.currentTemplate.data.anchor.field
      : null;

  const [mode, setMode] = useState<ExperiencePreviewMode>(
    experiencePreviewContext.mode
  );
  const [anchorDate, setAnchorDate] = useState<Timestamp | null>(
    experiencePreviewContext.anchorDate
  );
  const initUser = users
    ? users.find(
        (u) => u.id === experiencePreviewContext.currentParticipantUserId
      ) || null
    : null;
  const [sampleUser, setSampleUser] = useState<Doc<IUser> | null>(initUser);
  // The org user set is fetched on demand, so it may still be loading on the
  // first render: preselect the current participant once it arrives, unless
  // the admin has already picked (or cleared) a sample user.
  const sampleUserTouched = useRef(false);
  useEffect(() => {
    if (!sampleUserTouched.current && initUser) {
      setSampleUser((prev) => prev || initUser);
    }
  }, [initUser]);
  const autoCompleteUserTestData = async (participant: Doc<IUser> | null) => {
    sampleUserTouched.current = true;
    // Participant is deselected clear fields and ensure they're disabled.
    if (participant == null) {
      setSampleUser(null);
      return;
    }

    setSampleUser(participant);
    if (userAnchorField) {
      const fieldValue = getUserValue(participant.data, userAnchorField, now());
      if (isTimestamp(fieldValue)) {
        setAnchorDate(fieldValue);
      }
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (!users || error) {
    return (
      <SomethingWentWrongWithReport
        error={error}
        context={{ page: 'experience_preview' }}
      />
    );
  }

  return (
    <StandardDrawer open={open} onClose={onClose}>
      <CustomizePreviewHeader onClose={onClose} />

      <FormRow>
        <PreviewModeSelector value={mode} onChange={setMode} />
      </FormRow>

      <CustomizePreviewBody>
        {mode === 'INSTANCE' && (
          <FlexContainer
            direction="row"
            alignItems="center"
            justifyContent="center"
            paddingBottom={3}
          >
            <AutocompleteUser
              label={t('Sample Participant')}
              value={sampleUser}
              onChange={autoCompleteUserTestData}
              orgId={org.id}
            />
          </FlexContainer>
        )}

        {(mode === 'EXPERIENCE' || sampleUser) && (
          <FormRow>
            <DateTimeFieldNullable
              label={anchorLabel}
              value={anchorDate}
              onChange={setAnchorDate}
              tz={tz}
              fullWidth
              minutes
            />
          </FormRow>
        )}

        {mode === 'INSTANCE' && sampleUser && (
          <>
            {sampleUser.data.role && (
              <FormRow>
                <div>
                  <Typography component="h6" variant="caption">
                    {t('Role')}
                  </Typography>
                  <Typography variant="body1">
                    {sampleUser.data.role}
                  </Typography>
                </div>
              </FormRow>
            )}
            {sampleUser.data.team && (
              <FormRow>
                <div>
                  <Typography component="h6" variant="caption">
                    {t('Team')}
                  </Typography>
                  <Typography variant="body1">
                    {sampleUser.data.team}
                  </Typography>
                </div>
              </FormRow>
            )}
            {sampleUser.data.department && (
              <FormRow>
                <div>
                  <Typography component="h6" variant="caption">
                    {t('Department')}
                  </Typography>
                  <Typography variant="body1">
                    {sampleUser.data.department}
                  </Typography>
                </div>
              </FormRow>
            )}
            {sampleUser.data.location && (
              <FormRow>
                <div>
                  <Typography component="h6" variant="caption">
                    {t('Location')}
                  </Typography>
                  <Typography variant="body1">
                    {sampleUser.data.location}
                  </Typography>
                </div>
              </FormRow>
            )}
          </>
        )}

        <FlexContainer justifyContent="flex-end" fullWidth>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              const instanceModeInfo: Partial<ExperiencePreviewContext> =
                mode === 'INSTANCE'
                  ? {
                      currentParticipantUserId: sampleUser?.id ?? null
                    }
                  : {};

              onSave({
                ...experiencePreviewContext,
                ...instanceModeInfo,
                mode,
                anchorDate: anchorDate ?? now()
              });
            }}
          >
            {t('Save')}
          </Button>
        </FlexContainer>
      </CustomizePreviewBody>
    </StandardDrawer>
  );
};

const SharePreviewDrawer = ({
  open,
  experiencePreviewContext,
  share,
  onChange,
  onClose
}: {
  open: boolean;
  experiencePreviewContext: ExperiencePreviewContext;
  share: boolean;
  onChange: (share: boolean) => void;
  onClose: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const tpl = experiencePreviewContext.currentTemplate;

  const { enqueueSnackbarSuccess } = useSnackbar();

  const getShareUrl = (): string => {
    const url = new URL(window.location.origin);
    //DOn't pass anchorDate parameter for now
    // if (experiencePreviewContext.anchorDate) {
    // const anchorDateString = toMoment(
    //   experiencePreviewContext.anchorDate
    // ).toISOString();
    // url.searchParams.set('anchorDate', anchorDateString);
    // }
    url.pathname = `/experiencesPreview/${tpl.data.previewToken.token}`;
    return url.toString();
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(getShareUrl());
    enqueueSnackbarSuccess(t('Successfully copied sharing URL to clipboard!'));
  };

  const copyEmbededCode = () => {
    navigator.clipboard.writeText(
      createEmbeddableIframeForUrl(getShareUrl(), {
        width: '100%',
        height: '100%',
        minHeight: '600px'
      })
    );
    enqueueSnackbarSuccess(t('Embed code copied to clipboard!'));
  };

  return (
    <StandardDrawer open={open} onClose={onClose}>
      <FlexContainer justifyContent="space-between" marginBottom={2}>
        <Typography variant="h4">{t('Share Preview')}</Typography>
        <IconButton onClick={onClose}>
          <X size={20} />
        </IconButton>
      </FlexContainer>

      <FormRow>
        {t('Give colleagues an overview of your {{expName}} experience.', {
          expName: tpl.data.adminTitle || tpl.data.title
        })}
      </FormRow>

      <FormRow>
        <FormControlLabel
          control={<Switch checked={share} onChange={(e, v) => onChange(v)} />}
          label={t('Enable sharing')}
        />
      </FormRow>

      {share && (
        <>
          <FormRow>
            <InfoBox color="yellow">
              <FlexContainer alignItems="flex-start">
                <FlexItem flex={0}>
                  <AlertCircle size={20} />
                </FlexItem>
                <FlexItem flex={1}>
                  <FlexContainer direction="column" alignItems="flex-start">
                    <LightMdToJsx>
                      {t(
                        'Anyone with the link will be able to see this preview.'
                      )}
                    </LightMdToJsx>
                    <LightMdToJsx>
                      {t(
                        'However, only Admin users who are logged in to Dado will be able to preview what this would look like for an individual employee.'
                      )}
                    </LightMdToJsx>
                  </FlexContainer>
                </FlexItem>
              </FlexContainer>
            </InfoBox>
          </FormRow>

          <FormRow>
            <FlexContainer justifyContent="flex-end" fullWidth>
              <Button
                variant="outlined"
                color="primary"
                onClick={copyEmbededCode}
              >
                {t('Copy embed code')}
              </Button>
              <Button variant="contained" color="primary" onClick={copyUrl}>
                {t('Copy URL')}
              </Button>
            </FlexContainer>
          </FormRow>
        </>
      )}
    </StandardDrawer>
  );
};

const filterGroups = (
  groups: PreviewGroup[],
  itemTypesFilter: PreviewItemType[],
  actorFilter: string[]
): PreviewGroup[] => {
  return compact(
    groups.map((g) => {
      const batches = compact(
        g.batches.map((b) => {
          if (
            b.actorKey === 'AUTOMATION' &&
            !itemTypesFilter.includes(PreviewItemType.AUTOMATION)
          ) {
            return null;
          }
          if (
            b.actorKey !== 'AUTOMATION' &&
            !actorFilter.includes(b.actorKey)
          ) {
            return null;
          }
          const items = b.items.filter((item) =>
            itemTypesFilter.includes(item.type)
          );
          return items.length
            ? {
                ...b,
                items
              }
            : null;
        })
      );
      return batches.length ||
        (g.type !== PreviewGroupType.NORMAL &&
          g.type !== PreviewGroupType.TRIGGERED)
        ? {
            ...g,
            batches
          }
        : null;
    })
  );
};

const getActorDescriptorsForPArticipantName = (
  supportingActors: {
    [key: string]: ISupportingActor;
  },
  name: string,
  colors: Omit<ColorTheme, 'text'>
): { [key: string]: ActorDescriptor } => {
  const cs = [
    colors.blue.w400,
    colors.yellow.w400,
    colors.red.w400,
    colors.green.w400,
    colors.blue.w300,
    colors.yellow.w300,
    colors.red.w300,
    colors.green.w300
  ];
  const participantDescriptor: ActorDescriptor = {
    key: PARTICIPANT_ACTOR_KEY,
    label: name,
    color: colors.blue.w400,
    type: 'PARTICIPANT'
  };
  return keyBy(
    uniqBy(
      [
        participantDescriptor,
        // just take a guess here that manager and mentor are likely supporting actors, that
        // are probably more important than other roles
        ...sortBy(Object.values(supportingActors), (a) =>
          a.key === 'manager' || a.key === 'mentor' ? '0' : a.name
        ).map((a) => ({
          key: a.key,
          label: `${name}'s  ${a.name}`,
          color: a.color,
          type: a.type
        }))
      ],
      (d) => d.key
    ).map((d, i) => ({ ...d, color: d.color || cs[i % cs.length] })),
    (d) => d.key
  );
};

const replaceTokensMutable = (
  tokens: TokenDefinition[],
  replacements: { token: string; label: string }[]
) => {
  const replaceToken = (replacement: { token: string; label: string }) => {
    const tokenDefinition = tokens.find((t) => t.token === replacement.token);
    if (tokenDefinition) {
      tokenDefinition.label = replacement.label;
      tokenDefinition.group = '';
    }
  };
  replacements.forEach((r) => replaceToken(r));
};

const getTokenReplacements = (
  experiencePreview: ExperiencePreview
): { token: string; label: string }[] => {
  if (experiencePreview.mode === ExperiencePreviewMode.INSTANCE) {
    const participantReplacements = [
      {
        token: 'participant.fullName',
        label: experiencePreview.participantFullName
      },
      {
        token: 'participant.firstName',
        label: experiencePreview.participantName
      }
    ];

    const actorsFirstName = compact(
      Object.values(experiencePreview.supportingActors).map((a) => {
        if (a.key === PARTICIPANT_ACTOR_KEY) {
          return;
        }
        return {
          token: `${a.key}.firstName`,
          label: `${experiencePreview.participantName}'s ${a.name}`
        };
      })
    );

    return [...participantReplacements, ...actorsFirstName];
  }

  return [];
};

const joinWithFinalConjucntion = (
  values: string[],
  conjunction: string,
  emptyCase: string,
  useSerialComma: boolean
) => {
  const length = values.length;
  if (length <= 0) {
    return emptyCase;
  }
  if (length === 1) {
    return values[0];
  }
  if (length === 2) {
    return values.join(` ${conjunction} `);
  }
  if (useSerialComma) {
    const copy = values.slice();
    copy[length - 1] = `${conjunction} ${copy[length - 1]}`;
    return copy.join(', ');
  } else {
    return `${values.slice(0, -1).join(',')} ${conjunction} ${values.slice(
      -1
    )}`;
  }
};

// This is the inner body without sharing and customization controls
// As those might work differently in the shareable-version we don't include
// them here right away
export const ExperiencePreviewV2Body = ({
  experiencePreview
}: {
  experiencePreview: ExperiencePreview;
}) => {
  const { t } = useUnsafeTranslation();
  const {
    custom: { colors }
  } = useTheme();

  const actorDescriptorsTemplate = useActorDescriptorsFromTemplate(
    experiencePreview.supportingActors
  );

  const tokens = useTokenDefinitionsForExperience(
    experiencePreview.supportingActors,
    experiencePreview.customFields,
    { orgId: null, experienceTemplateId: null } // only used for rendering, so this is safe
  );

  const groups = experiencePreview.groups;

  const [state, setState] = useState<PreviewFilterState>({
    variant: 'normal',
    itemTypes: [
      PreviewItemType.MESSAGE,
      PreviewItemType.TASK,
      PreviewItemType.AUTOMATION
    ], // anchors will always be shown
    actorKeys: [...Object.keys(actorDescriptorsTemplate), 'AUTOMATION']
  });

  const itemTypesFilter = state.itemTypes;
  const actorFilter = state.actorKeys;
  const totalNumberOfActors = Object.keys(actorDescriptorsTemplate).length;

  const filteredGroups = useMemo(() => {
    if (
      itemTypesFilter.length === 3 &&
      actorFilter.length === totalNumberOfActors
    ) {
      return groups;
    }

    return filterGroups(groups, itemTypesFilter, actorFilter);
  }, [groups, itemTypesFilter, actorFilter, totalNumberOfActors]);

  const replacements = getTokenReplacements(experiencePreview);
  replaceTokensMutable(tokens, replacements);

  const actorsDescritorsInstance: { [key: string]: ActorDescriptor } | null =
    experiencePreview.mode === ExperiencePreviewMode.INSTANCE
      ? getActorDescriptorsForPArticipantName(
          experiencePreview.supportingActors,
          experiencePreview.participantName,
          colors
        )
      : null;

  if (experiencePreview.mode === ExperiencePreviewMode.INSTANCE) {
    console.log('missing actors', experiencePreview.missingActors);
  }

  const getMisigActorsNamesJoined = (
    experiencePreview: ExperiencePreview,
    conjuction: string
  ) => {
    if (experiencePreview.mode !== ExperiencePreviewMode.INSTANCE) {
      return '';
    }

    const actorNames = experiencePreview.missingActors.map(
      (key) => experiencePreview.supportingActors[key].name
    );
    return joinWithFinalConjucntion(actorNames, conjuction, '', true);
  };

  return (
    <>
      <Page width="M">
        <FlexContainer
          fullWidth
          marginBottom={2}
          direction="column"
          alignItems="start"
          spacing={3}
        >
          <div>
            <Typography variant="h6" color="textSecondary">
              {t('Experience preview')}
            </Typography>
            <Typography variant="h2">{experiencePreview.title}</Typography>
          </div>

          <PreviewFilters
            value={state}
            onChange={setState}
            actorDescriptors={actorDescriptorsTemplate}
          />

          {experiencePreview.mode === ExperiencePreviewMode.INSTANCE &&
            experiencePreview.missingActors.length > 0 && (
              <Alert
                severity="warning"
                variant="standard"
                iconMapping={{
                  warning: <Info fontSize="inherit" />
                }}
              >
                {t(
                  `This preview shows tasks and messages for ${getMisigActorsNamesJoined(
                    experiencePreview,
                    'and'
                  )}, even though your selected preview user currently has no ${getMisigActorsNamesJoined(
                    experiencePreview,
                    'or'
                  )}  assigned`
                )}
              </Alert>
            )}

          <FlexContainer
            fullWidth
            direction="column"
            alignItems="start"
            spacing={0}
          >
            {filteredGroups.map((group, i) => (
              <PreviewGroupComponent
                key={`${
                  experiencePreview.mode === ExperiencePreviewMode.INSTANCE
                    ? experiencePreview.id
                    : 'NO_SPECIFIC_USER'
                }----${group.ts}---${
                  group.triggeredBy?.map((x) => x.id).join('--') || ''
                }---${group.batches.map(previewBatchToKey).join('--')}`}
                group={group}
                org={experiencePreview.org}
                actorDescriptors={
                  actorsDescritorsInstance || actorDescriptorsTemplate
                }
                variant={state.variant}
                isLast={filteredGroups.length - 1 === i}
                anchorName={experiencePreview.anchorName}
                taskBuilderContext={getMiniMalTaksBuilderContext(
                  experiencePreview
                )}
                anchorFields={getAnchorFields(experiencePreview.customFields)}
                tokens={tokens}
                customFields={experiencePreview.customFields}
                mode={experiencePreview.mode}
              />
            ))}
          </FlexContainer>
        </FlexContainer>
      </Page>
      <LogoContainer>
        <img
          src={'/images/dado-logo/created-with-dado-logo.svg'}
          height={48}
          alt="Dado"
        />
      </LogoContainer>
    </>
  );
};

export const ExperiencePreviewV2PageUnauthenticated = ({
  accessToken
}: {
  accessToken: string;
}) => {
  // accessToken will the way to retrieve this data from the server
};

export const ExperiencePreviewV2PageAuthenticated = ({
  accessToken
}: {
  accessToken: string;
}) => {
  // accessToken will the way to retrieve this data from the server
  // The rest of it is not too dissimilar from the Unauthenticated page - it just has more features in respect
  // to Configurability etc
  // There's a case to be made to just redirect to wherever the dialog lives.
  // There's one complication: We also need to make sure that the user has the necessary right to access this template
  // Maybe that's the way to go:
  // - If we are logged in, we check access rights.
  //   - If user is allowed to see and edit the template, we redirect them to the dialog version
  // - If not we redirect them to the unauthenticated version of the page
};

export const ExperiencePreviewV2Dialog = ({
  tpl,
  org,
  open,
  experiencePreviewContext,
  onChange,
  onClose
}: {
  open: boolean;
  tpl: Doc<IExperienceTemplate>;
  org: IOrganization;
  experiencePreviewContext: ExperiencePreviewContext;
  onChange: (ctx: ExperiencePreviewContext) => void;
  onClose: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const customizePreviewDrawer = useDialogState();
  const sharePreviewDrawer = useDialogState();
  const [users, loading, error] = useUsersByOrgId(org.id);

  const [participant, setParticipant] = useState<Doc<IUser> | null>();
  useEffect(() => {
    const loadUser = async () => {
      if (experiencePreviewContext.currentParticipantUserId) {
        const newParticipant = await getUser(
          experiencePreviewContext.currentParticipantUserId
        );
        setParticipant(newParticipant);
      } else {
        setParticipant(null);
      }
    };
    if (experiencePreviewContext.mode === 'INSTANCE') {
      loadUser();
    } else {
      setParticipant(null);
    }
  }, [experiencePreviewContext]);

  const experiencePreview = useMemo(() => {
    const anchorDate = experiencePreviewContext.anchorDate;
    const previewOptions: PreviewOptions = participant
      ? {
          mode: ExperiencePreviewMode.INSTANCE,
          usersInOrg: users || [],
          participantUser: participant,
          anchorDate
        }
      : {
          mode: ExperiencePreviewMode.EXPERIENCE,
          anchorDate
        };
    return getExperiencePreview(tpl, org, previewOptions, t);
  }, [tpl, org, experiencePreviewContext, participant, users]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        onClick={(ev) => ev.stopPropagation()}
        fullScreen
      >
        <DialogActionsHeader
          onClose={onClose}
          left={<OrgLogo d={experiencePreview.org} />}
        >
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ExternalLink size={16} />}
            onClick={sharePreviewDrawer.openDialog}
          >
            {t('Share')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Settings size={16} />}
            onClick={customizePreviewDrawer.openDialog}
          >
            {t('Customize Preview')}
          </Button>
        </DialogActionsHeader>
        <DialogContent>
          <ExperiencePreviewV2Body experiencePreview={experiencePreview} />
        </DialogContent>
      </Dialog>
      {customizePreviewDrawer.dialogOpen && (
        <CustomizePreviewDrawer
          org={org}
          open={true}
          experiencePreviewContext={experiencePreviewContext}
          onClose={customizePreviewDrawer.closeDialog}
          onSave={(ctx) => {
            onChange(ctx);
            customizePreviewDrawer.closeDialog();
          }}
        />
      )}
      {sharePreviewDrawer.dialogOpen && (
        <SharePreviewDrawer
          open={true}
          experiencePreviewContext={experiencePreviewContext}
          share={tpl.data.previewToken.active}
          onChange={(share) =>
            updateDoc(tpl, () => {
              const token =
                tpl.data.previewToken?.token || generateAccessToken();
              const previewToken: IExperienceTemplate['previewToken'] = {
                token,
                active: share
              };

              return { previewToken };
            })
          }
          onClose={sharePreviewDrawer.closeDialog}
        />
      )}
    </>
  );
};

const useExperiencePreviewParams = () => {
  const parameterDelimiter = '|';

  const modeParameterMap: { [key in ExperiencePreviewMode]: string } = {
    [ExperiencePreviewMode.EXPERIENCE]: 'e',
    [ExperiencePreviewMode.INSTANCE]: 'i'
  };

  return useQueryParam<ExperiencePreviewParams | null>(
    'preview',
    (p) => {
      if (!p) return null;

      const [modeParam, userId, anchorDateParam] = p.split(parameterDelimiter);

      const mode: ExperiencePreviewMode = Object.entries(modeParameterMap).find(
        ([, p]) => p === modeParam
      )![0] as ExperiencePreviewMode;

      const currentParticipantUserId = userId || null;

      const anchorDate: Timestamp | null = anchorDateParam
        ? tsFromMillis(Number.parseInt(anchorDateParam))
        : null;

      return {
        mode,
        currentParticipantUserId,
        anchorDate
      };
    },
    (ctx) => {
      if (!ctx) return undefined;

      return [
        modeParameterMap[ctx.mode],
        ctx.currentParticipantUserId ?? '',
        ctx.anchorDate?.toMillis().toString()
      ].join(parameterDelimiter);
    }
  );
};

export const ExperiencePreviewV2Button: React.FC<{
  d: Doc<IExperienceTemplate>;
  startIcon?: React.ReactNode;
  variant?: ButtonProps['variant'];
  color?: ButtonProps['color'];
  size?: ButtonProps['size'];
}> = ({ d, variant = 'outlined', startIcon, color, size, children }) => {
  const { org } = useCurrentUser();
  const [
    experiencePreviewParams,
    setExperiencePreviewParams
  ] = useExperiencePreviewParams();

  const experiencePreviewContext = useMemo<ExperiencePreviewContext | null>(() => {
    if (!experiencePreviewParams) return null;

    return {
      ...experiencePreviewParams,
      currentTemplate: d
    };
  }, [experiencePreviewParams, d]);

  return (
    <>
      <Button
        variant={variant}
        startIcon={startIcon}
        color={color}
        size={size}
        onClick={() =>
          setExperiencePreviewParams({
            mode: ExperiencePreviewMode.EXPERIENCE,
            currentParticipantUserId: null,
            anchorDate: calculateInitalAnchorDate(d, org.tz)
          })
        }
      >
        {children}
      </Button>
      {experiencePreviewContext && (
        <ExperiencePreviewV2Dialog
          open={true}
          org={org}
          experiencePreviewContext={experiencePreviewContext}
          onChange={(ctx) => setExperiencePreviewParams(ctx)}
          onClose={() => setExperiencePreviewParams(null)}
          tpl={d}
        />
      )}
    </>
  );
};
